/* eslint-disable no-restricted-globals */
import { useMemo, useEffect, useState, useRef, useCallback } from 'react'
import * as actions from '@actions'
import * as store from '@store'
import useHotkeys from '@hook/useHotkeys'
import useBetterSearchParams from '@hook/useBetterSearchParams'
import useSortableTableHeaders from '@/hook/useSortableTableHeaders'
import { useWireValue, useWireState } from '@forminator/react-wire'
import { sortByMultiple } from '@/utils'
import { MAIN_TAB, RUNSHEET_HEADERS } from '@/constants'

const headers = [
    ['document.name', true, 'small-column'],
    ['grantors', false, 'big-column'],
    ['grantees',  false, 'big-column'],
    ['fullGrantor', false, 'big-column'],
    ['fullGrantee', false, 'big-column'],
    ['bookVolume', true, 'small-column'],
    ['bookPage', true],
    ['bookType', true, 'small-column'],
    ['instrumentNumber', true, 'medium-column'],
    ['instrumentType', true, 'medium-column'],
    ['instrumentDate', true, 'small-column'],
    ['fileDate', true, 'small-column'],
    ['legalDescriptions', true, 'dynamic-column'],
    ['comment', false, 'dynamic-column'],
    ['recordingDate', false, 'dynamic-column'],
    ['notes', false, 'dynamic-column'],
    ['tabs', false, 'dynamic-column'],
    
]

const RunsheetDocumentsTableViewModel = runsheetDocs => {
    
    const tableRef = useRef()
    
    const { searchParams, updateSearchParams } = useBetterSearchParams()
    
    const [sortedDocs, setSortedDocs] = useState([])
    
    const runsheetHeaders = useWireValue(store.runsheetHeaders)
    const [runsheetOrder, setRunsheetOrder] = useWireState(store.runsheetOrder)
    const tabs = useWireValue(store.runsheetTabs)
    
    const activeTab = useMemo(() => searchParams.get('activeTab') || MAIN_TAB, [searchParams])
    const currentTab = useMemo(() => tabs.find(t => t.id === activeTab) || tabs[0], [tabs, activeTab])
    
    const { onHeaderClick, orderDirection, orderField } = useSortableTableHeaders(headers,
        runsheetOrder?.orderField, runsheetOrder?.orderDirection)
    
    const sortedHeaders = useMemo(() => (
        runsheetHeaders.length
            ? runsheetHeaders.map(dh => headers.find(h => h[0] === dh)).filter(rh => !!rh)
            : headers
    ), [runsheetHeaders])
    
    
    useEffect(() => {
        
        updateSearchParams({
            field: orderField,
            order: orderDirection,
        })
        
        setRunsheetOrder({ orderField, orderDirection })
        
    }, [searchParams.get('activeTab'), orderDirection, orderField])
    
    useEffect(() => {
        
        if (runsheetDocs) {
            
            const docsWithTabs = runsheetDocs.map(doc => {
                const documentTabs = tabs
                    .filter(tab => 
                        tab.joins?.some(join => join.runsheetDocumentValueId === doc.runsheetDocumentValueId),
                    )
                    .map(tab => ({ id: tab.id, name: tab.name }))
                
                return { ...doc, tabs: documentTabs }
            })
            
            const field = searchParams.get('field')
            const direction = searchParams.get('order')
            
            if (!field?.length || !direction?.length) {
                // if somehow i dont have sorting, i still need to set those documents when tabs are changing
                setSortedDocs(docsWithTabs)
                return
            }
            
            let fieldOrList
            
            if (field.startsWith('document')) {
                fieldOrList = field
            } else if (field === 'legalDescriptions') {
                fieldOrList = 'value.legalDescriptions[0].summary'
            } else {
                fieldOrList = (() => {
                    if (['instrumentDate', 'fileDate'].includes(field)) {
                        return [`value.${field}`, 'value.recordingDate']
                    }
                    
                    return `value.${field}`
                })()
            }
            
            
            const type = ['instrumentDate', 'fileDate', 'recordingDate'].includes(field)
                ? 'date'
                : 'natural'
            
            const sorted = sortByMultiple(
                [...docsWithTabs],
                fieldOrList,
                type,
                direction === 'ASC',
            )
            
            setSortedDocs(sorted)
        }
    }, [runsheetDocs, searchParams])
    
    const updateRunsheetValue = (rsDocJoin, property) => async value => {
        
        const rsDocValue = rsDocJoin.value = rsDocJoin.value || {}
        
        // undefined values are removed on API call - keep empty strings
        rsDocValue[property] = value ?? null
        
        await actions.updateRunsheetValue(
            rsDocValue.id,
            rsDocJoin.runsheetId,
            rsDocJoin.documentId,
            property,
            value)
        
    }
    
    const focusOnElement = element => {
        
        const targetInput = element.querySelector('input')
        const targetContentEditable = element.querySelector('div[contenteditable="true"]')
        
        if (targetInput)
            targetInput.focus()
        else if (targetContentEditable)
            targetContentEditable.focus()
        
    }
    
    const moveToNextRowSameColumn = e => {
        
        if (!e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
            
            
            const activeElement = document.activeElement
            const currentRow = activeElement.closest('tr')
            const cellIndex = activeElement.closest('td').cellIndex
            const nextRow = currentRow.nextElementSibling
            
            if (nextRow) {
                const targetCell = nextRow.children[cellIndex]
                
                focusOnElement(targetCell)
            }
            
        }
        
    }
    
    // Comment this code as isn't needed for now (but just for now)
    // const moveToNextRowFirstColumn = () => {
    //     // eslint-disable-next-line
    //     const activeElement = document.activeElement
    //     const currentRow = activeElement.closest('tr')
    //     const nextRow = currentRow.nextElementSibling
    //     if (nextRow) {
    //         const targetCell = nextRow.children[2]
    //         focusOnElement(targetCell)
    //     }
    // }
    
    const moveToPreviousCell = () => {
        
        
        const activeElement = document.activeElement
        const currentCell = activeElement.closest('td')
        
        let previousCell = currentCell.previousElementSibling
            ?? activeElement.closest('tr').previousElementSibling.lastChild
        
        while (previousCell) {
            
            const targetElement = previousCell.querySelector('input, div[contenteditable="true"]')
            
            if (targetElement) {
                focusOnElement(previousCell)
                return
            }
            
            previousCell = previousCell.previousElementSibling
            
        }
        
    }
    
    const moveToPreviousRowSameColumn = () => {
        
        
        const activeElement = document.activeElement
        const currentRow = activeElement.closest('tr')
        const cellIndex = activeElement.closest('td').cellIndex
        const previousRow = currentRow.previousElementSibling
        
        if (previousRow) {
            const targetCell = previousRow.children[cellIndex]
            
            focusOnElement(targetCell)
        }
        
    }
    
    useHotkeys([
        {
            key: 'Enter',
            modifier: 'shiftKey',
            handler: moveToPreviousRowSameColumn,
        },
        {
            key: 'Enter',
            handler: moveToNextRowSameColumn,
        },
        {
            key: 'Tab',
            modifier: 'shiftKey',
            handler: moveToPreviousCell,
        },
    ])
    
    const getHeaderLabel = useCallback(key => {
        
        const { label } = RUNSHEET_HEADERS.find(h => h.id === key)
        
        return typeof label === 'string' ? label : label[currentTab?.type || 'deed']
        
    }, [currentTab])
    
    return {
        
        // Constants
        headers,
        
        // Refs
        tableRef,
        
        // State
        sortedDocs,
        setSortedDocs,
        
        // Global State
        runsheetHeaders,
        runsheetOrder,
        setRunsheetOrder,
        tabs,
        
        // Memos
        activeTab,
        currentTab,
        sortedHeaders,
        
        // Hooks
        searchParams,
        updateSearchParams,
        onHeaderClick,
        orderDirection,
        orderField,
        
        // Methods
        updateRunsheetValue,
        focusOnElement,
        moveToNextRowSameColumn,
        moveToPreviousCell,
        moveToPreviousRowSameColumn,
        getHeaderLabel,
        
    }
    
}

export default RunsheetDocumentsTableViewModel
